import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import { computed } from 'mobx';

import type { FavoriteData, ReferralFavoriteDataParams } from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';
import { SimpleList } from '@store/models/SimpleList';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import { assertIsDefined } from '@utils/assertIsDefined';
import { translate } from '@chaika/i18n/helper';

interface FilterParams {
    groupReferral?: boolean;
}

export class ReferralFavoriteList extends SimpleList<
    FavoriteData<ReferralFavoriteDataParams | ReferralFavoriteDataParams[]>,
    FilterParams
> {
    @computed
    public get serviceIds(): string[] {
        return toArrayFromIterable<string>(this.serviceIdsSet);
    }

    @computed
    public get ungroupIds(): string[] {
        return this.getFilteredValuesIds({ groupReferral: false });
    }

    @computed
    public get groupIds(): string[] {
        return this.getFilteredValuesIds({ groupReferral: true });
    }

    @computed
    public get groups(): FavoriteData<ReferralFavoriteDataParams | ReferralFavoriteDataParams[]>[] {
        return this.getFilteredValues({ groupReferral: true });
    }

    public getItemTitle(id: string, onlySerialNumber?: boolean): string {
        const { name, data } = this.getExisting(id);

        return isArray(data) ? name : this.getServiceTitle(data, onlySerialNumber);
    }

    public getGroupItemTitle(groupId: string, serviceId: string, onlySerialNumber?: boolean): string {
        const data = this.getReferralFavoriteDataByGroupId(groupId, serviceId);

        return this.getServiceTitle(data, onlySerialNumber);
    }

    public getItemContent(id: string): string {
        const { name, data } = this.getExisting(id);
        return isArray(data) ? name : (store.serviceStore.serviceList.get(data.serviceId)?.name ?? '');
    }

    public getGroupItemContent(groupId: string, serviceId: string): string {
        const data = this.getReferralFavoriteDataByGroupId(groupId, serviceId);
        return store.serviceStore.serviceList.get(data.serviceId)?.name ?? '';
    }

    public isServiceFavorite(serviceId: string): boolean {
        return this.serviceIdsSet.has(serviceId);
    }

    public getActiveAndArchivedReferralCounts(): { activeCount: number; archivedCount: number } {
        const data = this.values.flatMap(v => (isArray(v.data) ? v.data : [v.data]));

        return this.countActiveAndArchived(data);
    }

    public getActiveAndArchivedGroupCounts(groupId: string): { activeCount: number; archivedCount: number } {
        const { data } = this.getExisting(groupId);

        if (!isArray(data)) return { activeCount: 0, archivedCount: 0 };

        return this.countActiveAndArchived(data);
    }

    public getGroupReferralIds(id: string): string[] {
        const { data } = this.getExisting(id);
        const serviceIds = (isArray(data) ? data : []).map(({ serviceId }) => serviceId);

        return this.getActiveServiceIds(serviceIds);
    }

    public getReferralFavoriteDataByGroupId(id: string, serviceId?: string): ReferralFavoriteDataParams {
        const { data } = this.getExisting(id);

        if (!isArray(data)) {
            return data;
        }

        const favoriteData = data.find(params => params.serviceId === serviceId);
        assertIsDefined(favoriteData, `Service ${serviceId} belongs to the referral favorite group ${id}`);
        return favoriteData;
    }

    public getReferralFavoriteData(serviceId: string): ReferralFavoriteDataParams {
        const data: ReferralFavoriteDataParams[] = [];

        this.values.forEach(value => {
            if (isArray(value.data)) {
                data.push(...value.data);
            } else {
                data.push(value.data);
            }
        });

        const favoriteData = data.find(item => item.serviceId === serviceId);
        assertIsDefined(favoriteData, `Service ${serviceId} is one of the referral favorites`);
        return favoriteData;
    }

    public isLabServiceArchived(data: ReferralFavoriteDataParams): boolean {
        const { laboratoryStore } = store;

        if (data.laboratoryServiceId) {
            return !laboratoryStore.laboratoryServiceList.has(data.laboratoryServiceId);
        }

        if (data.laboratoryService?.laboratoryId) {
            const services = laboratoryStore.getLaboratoryServicesByServiceId(data.serviceId);
            const { laboratoryId, labCode, bioCode } = data.laboratoryService;

            return !services.some(
                s => s.laboratoryId === laboratoryId && s.labCode === labCode && s.bioCode === bioCode,
            );
        }

        return false;
    }

    public getGroupActiveReferralIds(id: string): string[] {
        const { data } = this.getExisting(id);
        if (!isArray(data)) return [];
        const serviceIds = data.filter(item => !this.isLabServiceArchived(item)).map(({ serviceId }) => serviceId);

        return this.getActiveServiceIds(serviceIds);
    }

    protected filterValue(
        { data }: FavoriteData<ReferralFavoriteDataParams | ReferralFavoriteDataParams[]>,
        { groupReferral }: FilterParams,
    ): boolean {
        return !isNil(groupReferral) ? (groupReferral ? isArray(data) : !isArray(data)) : true;
    }

    @computed
    private get serviceIdsSet(): Set<string> {
        const serviceIds = flatten(
            this.values.map(({ data }) => (isArray(data) ? data.map(({ serviceId }) => serviceId) : data.serviceId)),
        );
        const activeServiceIds = this.getActiveServiceIds(serviceIds);

        return new Set(activeServiceIds);
    }

    private countActiveAndArchived(items: ReferralFavoriteDataParams[]): {
        activeCount: number;
        archivedCount: number;
    } {
        let archivedCount = 0;
        const activeServiceIds: string[] = [];

        for (const item of items) {
            if (this.isLabServiceArchived(item)) {
                if (store.serviceStore.serviceList.isActive(item.serviceId)) {
                    archivedCount++;
                }
            } else {
                activeServiceIds.push(item.serviceId);
            }
        }

        return {
            activeCount: this.getActiveServiceIds(activeServiceIds).length,
            archivedCount,
        };
    }

    private getActiveServiceIds(serviceIds: string[]): string[] {
        return serviceIds.filter(id => store.serviceStore.serviceList.isActive(id));
    }

    private getServiceTitle(
        { serviceId, laboratoryServiceId }: ReferralFavoriteDataParams,
        onlyCode?: boolean,
    ): string {
        const { serviceStore, laboratoryStore } = store;
        const service = serviceStore.serviceList.get(serviceId);
        let labTitle;
        let isLabService = false;

        if (!onlyCode && service && serviceStore.isServiceLaboratory(service.id)) {
            isLabService = true;
            const laboratoryId = laboratoryServiceId
                ? laboratoryStore.laboratoryServiceList.get(laboratoryServiceId)?.laboratoryId
                : undefined;

            labTitle = laboratoryId ? laboratoryStore.laboratoryList.get(laboratoryId)?.name : undefined;
        }

        return onlyCode
            ? String(service?.code)
            : compact([service?.code, isLabService && translate('laboratory'), labTitle]).join(' ');
    }
}
